import type {
  ConfirmationMethod,
  ConfirmationStatus,
  JobAppointment,
  JobAppointmentStatus,
  RescheduleRequest,
} from '@/types/jobAppointment';

export const READY_TO_DISPATCH_LABEL = 'Ready to dispatch';
export const NO_SHOW_LABEL = 'No-show';

/**
 * A one-off visit the customer was not there for. The server records it as a
 * skipped visit carrying a no-show reason; it reads as a no-show, never as a
 * skip, which only a recurring series has.
 */
export function isNoShowVisit(
  appointment: Pick<JobAppointment, 'status' | 'noShowReason'>,
): boolean {
  return appointment.status === 'SKIPPED' && Boolean(appointment.noShowReason);
}

export function visitLifecycleStatusLabel(
  status: JobAppointmentStatus,
  noShowReason?: string | null,
): string {
  if (status === 'CONFIRMED') return READY_TO_DISPATCH_LABEL;
  if (isNoShowVisit({ status, noShowReason })) return NO_SHOW_LABEL;
  return status
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * True when a confirmation request actually went out and the customer still
 * hasn't answered it (or answered no).
 *
 * `NOT_REQUESTED` is NOT an unanswered request — nobody asked the customer
 * anything. Folding it in with PENDING/NO_RESPONSE made every visit on a tenant
 * that runs no confirmation cadence look like the customer was ignoring us,
 * which is the "forcing a confirmation we never received" complaint. The
 * backend now promotes NOT_REQUESTED → PENDING when a JOB_CONFIRMATION step is
 * dispatched, so PENDING here really does mean "we asked, they're silent".
 *
 * BR-77 — `RESCHEDULE_REQUESTED` is excluded: the customer DID answer, they
 * asked for another time. Nagging "customer has not confirmed" over a request
 * we are sitting on blames them for our silence, and it would bury the request
 * behind the wrong prompt. That state has its own, louder signal.
 */
export function isAwaitingCustomerConfirmation(
  confirmation: ConfirmationStatus | null | undefined,
): boolean {
  const status = confirmation ?? 'NOT_REQUESTED';
  return status !== 'NOT_REQUESTED' && status !== 'CONFIRMED' && status !== 'RESCHEDULE_REQUESTED';
}

export function confirmedLifecycleNextStepCopy(
  confirmation: ConfirmationStatus | null | undefined,
): string {
  return confirmation === 'CONFIRMED'
    ? 'Customer confirmed — ready to dispatch.'
    : 'Ready to dispatch.';
}

export const RESCHEDULE_REQUESTED_LABEL = 'Reschedule requested';

/**
 * BR-77 — the one place that decides whether a visit shows a pending
 * reschedule request. The server only sends `rescheduleRequest` while the
 * request is outstanding (it retires on a move, cancel, completion or staff
 * confirm), so this never has to reason about staleness — but it is a named
 * predicate so the badge, the visit next-step card and the job overview can
 * never disagree about which visits are flagged.
 */
export function getPendingRescheduleRequest(
  appointment: Pick<JobAppointment, 'rescheduleRequest'> | null | undefined,
): RescheduleRequest | null {
  return appointment?.rescheduleRequest ?? null;
}

/**
 * One line describing what the customer asked for. Their suggested windows are
 * free text they typed ("tomorrow afternoon"), so they are shown verbatim —
 * resolving them into real slots is the dispatcher's job.
 */
export function summarizeRescheduleRequest(request: RescheduleRequest): string {
  const windows = request.preferredWindows.filter((window) => window.trim().length > 0);
  const parts: string[] = [];
  parts.push(
    windows.length > 0
      ? `Customer asked for: ${windows.join('; ')}`
      : 'Customer asked for a different time',
  );
  if (request.note) parts.push(`Note: ${request.note}`);
  return parts.join(' · ');
}

/**
 * The confirmation states an OPERATOR may set, with their picker labels.
 *
 * BR-77 — `RESCHEDULE_REQUESTED` is deliberately absent. It is customer-driven,
 * and it is only half the state: the request also carries
 * `rescheduleRequestedAt` / `rescheduleRequestedForStartAt`, which are what
 * `isReschedulePending()` derives from. Hand-setting the flag here would write a
 * visit whose status says "requested" while the predicate says nothing is
 * pending — the denormalised divergence this work removed. The backend agrees:
 * `UpdateConfirmationDto` does not accept it either.
 *
 * Lives here rather than beside the form so the picker's options and the
 * predicate that guards the save payload cannot drift apart.
 */
export const OPERATOR_SETTABLE_CONFIRMATION_STATUSES: ReadonlyArray<{
  value: ConfirmationStatus;
  label: string;
}> = [
  { value: 'NOT_REQUESTED', label: 'Not requested' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'NO_RESPONSE', label: 'No response' },
];

/** True when the current value is one an operator may pick. */
export function isOperatorSettableConfirmationStatus(
  status: ConfirmationStatus | null | undefined,
): boolean {
  return OPERATOR_SETTABLE_CONFIRMATION_STATUSES.some((option) => option.value === status);
}

/**
 * The confirmation METHODS an operator may set — how the visit is configured to
 * be confirmed. Machine writers used to stamp a RESPONSE channel here
 * ('CLICK_TO_CONFIRM' / 'SMS_REPLY'), which is both outside this domain and a
 * destruction of the operator's choice; those writes are gone and the rows are
 * repaired. The guard stays so a value the operator never chose can never be
 * echoed back into a save again.
 *
 * Lives beside the statuses so the picker's options and the predicate guarding
 * the save payload cannot drift apart.
 */
export const OPERATOR_SETTABLE_CONFIRMATION_METHODS: ReadonlyArray<{
  value: ConfirmationMethod;
  label: string;
}> = [
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'BOTH', label: 'SMS + Email' },
];

/** True when the current value is a method an operator may pick. */
export function isOperatorSettableConfirmationMethod(
  method: ConfirmationMethod | null | undefined,
): boolean {
  return OPERATOR_SETTABLE_CONFIRMATION_METHODS.some((option) => option.value === method);
}

/**
 * A visit's close-out outcome, as something a person would say.
 *
 * `outcome` holds either a note the office typed or one of the two codes the
 * close-out dialog sends (`DONE`, `CREATE_SECOND_VISIT`). The codes were
 * rendered verbatim, so a completed visit that spawned a follow-up read
 * "CREATE_SECOND_VISIT" on the card. `DONE` returns null: the Completed badge
 * beside it already says exactly that, and a second line repeating it is noise.
 * Anything that is not a known code is the office's own note, shown as written.
 *
 * `CREATE_SECOND_VISIT` is the API's name for "add a follow-up visit", from any
 * visit — closing out the second visit this way adds the third. The copy says
 * "another visit", as the close-out dialog's button does, never an ordinal.
 */
export function formatVisitOutcome(
  outcome: string | null | undefined,
  technicianName?: string | null,
): string | null {
  const value = (outcome ?? '').trim();
  if (!value) return null;
  switch (value.toUpperCase()) {
    case 'DONE':
      return null;
    case 'CREATE_SECOND_VISIT': {
      const who = (technicianName ?? '').trim();
      return who ? `${who} created another visit.` : 'Another visit was created.';
    }
    default:
      return value;
  }
}
